import React, { useState, useEffect, useRef } from 'react';
import LogoLoop from '../LogoLoop/LogoLoop';
import './AppleWatchFloatingCircle.css';
import CustomAlert from './CustomAlert'; // Certifique-se que o caminho está correto

const AppleWatchFloatingCircle = ({ 
  count = 6,
  icons = [],
  position = 'bottom', // 'top' ou 'bottom'
  carouselProps = {}
}) => {
  const [circles, setCircles] = useState([]);
  const [alertInfo, setAlertInfo] = useState({ show: false, title: '', content: null, url: '' });
  const [hoveredCircle, setHoveredCircle] = useState(null);
  const [popupCircle, setPopupCircle] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, side: 'right' });
  const [screenSize, setScreenSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isCarouselMode, setIsCarouselMode] = useState(window.innerWidth < 1200);
  const [carouselScroll, setCarouselScroll] = useState(0);
  const circleRefs = useRef({});
  const containerRef = useRef(null);
  const carouselRef = useRef(null);

  // Função para calcular tamanhos responsivos baseados na tela
  const getResponsiveSizes = () => {
    const { width, height } = screenSize;
    const minDimension = Math.min(width, height);
    
    // Calcular tamanho base baseado na menor dimensão da tela
    let baseSize, iconSize, scaleRange;
    
    if (width < 480) {
      // Mobile muito pequeno
      baseSize = 35;
      iconSize = 60;
      scaleRange = 0.2;
    } else if (width < 768) {
      // Mobile
      baseSize = 45;
      iconSize = 64;
      scaleRange = 0.25;
    } else if (width < 1024) {
      // Tablet
      baseSize = 55;
      iconSize = 68;
      scaleRange = 0.3;
    } else if (width < 1440) {
      // Desktop pequeno
      baseSize = 85;
      iconSize = 95;
      scaleRange = 0.35;
    } else if (width < 1920) {
      // Desktop médio
      baseSize = 96;
      iconSize = 128;
      scaleRange = 0.55;
    } else {
      // Desktop grande/4K
      baseSize = 108;
      iconSize = 196;
      scaleRange = 0.85;
    }
    
    // Ajustar baseado na proporção da tela
    const aspectRatio = width / height;
    const sizeMultiplier = aspectRatio > 1.5 ? 1.1 : aspectRatio < 0.8 ? 0.9 : 1.0;
    
    return {
      baseSize: Math.round(baseSize * sizeMultiplier),
      iconSize: Math.round(iconSize * sizeMultiplier),
      scaleRange: scaleRange * sizeMultiplier,
      hoverScale: 1.2
    };
  };

  // Função para calcular propriedades responsivas do carrossel
  const getCarouselResponsiveProps = () => {
    const { width, height } = screenSize;
    
    let logoHeight, gap, speed;
    
    if (width < 480) {
      // Mobile muito pequeno
      logoHeight = 50;
      gap = 30;
      speed = 25;
    } else if (width < 768) {
      // Mobile
      logoHeight = 60;
      gap = 40;
      speed = 30;
    } else if (width < 1024) {
      // Tablet
      logoHeight = 70;
      gap = 50;
      speed = 35;
    } else if (width < 1440) {
      // Desktop pequeno
      logoHeight = 80;
      gap = 60;
      speed = 40;
    } else {
      // Desktop grande
      logoHeight = 90;
      gap = 70;
      speed = 45;
    }
    
    // Ajustar baseado na altura da tela para telas muito baixas
    if (height < 600) {
      logoHeight = Math.max(40, logoHeight * 0.7);
      gap = Math.max(20, gap * 0.7);
    }
    
    return {
      logoHeight,
      gap,
      speed
    };
  };

  // Função para calcular posição do popup dinamicamente
  const calculatePopupPosition = (circleId) => {
    const circleElement = circleRefs.current[circleId];
    const containerElement = containerRef.current;
    
    if (!circleElement || !containerElement) return;
    
    const circleRect = circleElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    
    // Calcular posição relativa ao container
    const relativeTop = circleRect.top - containerRect.top;
    const relativeLeft = circleRect.left - containerRect.left;
    
    // Calcular centro do círculo
    const circleCenterX = relativeLeft + (circleRect.width / 2);
    const circleCenterY = relativeTop + (circleRect.height / 2);
    
    // Determinar lado baseado na posição na tela
    const isLeftSide = circleCenterX < (containerRect.width / 2);
    const side = isLeftSide ? 'right' : 'left';
    
    // Calcular posição do popup baseada no tamanho da tela
    const popupWidth = window.innerWidth < 768 ? 140 : 180;
    const popupHeight = window.innerWidth < 768 ? 40 : 50;
    const distance = 40; // Distância fixa de 40px do elemento
    
    let popupLeft, popupTop;
    
    if (isLeftSide) {
      // Popup à direita do círculo - 40px de distância
      popupLeft = circleCenterX + (circleRect.width / 2) + distance;
    } else {
      // Popup à esquerda do círculo - 40px de distância
      popupLeft = circleCenterX - (circleRect.width / 2) - popupWidth - distance - 10;
    }
    
    popupTop = circleCenterY - (popupHeight / 2); // Centralizar verticalmente
    
    // Garantir que o popup não saia da tela
    const maxLeft = containerRect.width - popupWidth - 10;
    const minLeft = 10;
    const maxTop = containerRect.height - popupHeight - 10;
    const minTop = 10;
    
    popupLeft = Math.max(minLeft, Math.min(maxLeft, popupLeft));
    popupTop = Math.max(minTop, Math.min(maxTop, popupTop));
    
    setPopupPosition({
      top: popupTop,
      left: popupLeft,
      side: side
    });
  };

  // Função de clique para os ícones (modo desktop)
  const handleIconClick = (circle) => {
    console.error(`Clicou no ícone: ${circle.name} - ${circle.title}`);
    
    // No modo desktop, usar popup tradicional
    if (!isCarouselMode) {
      const newPopupCircle = popupCircle === circle.id ? null : circle.id;
      setPopupCircle(newPopupCircle);
      
      if (newPopupCircle !== null) {
        // Calcular posição do popup
        setTimeout(() => {
          calculatePopupPosition(circle.id);
        }, 10);
      }
    }
  };

  // Função para fechar o popup
  const handleClosePopup = () => {
    setPopupCircle(null);
  };

  // Funções do carrossel
  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return;

    const carousel = carouselRef.current;
    const itemWidth = 150; // Largura aproximada de cada item incluindo gap (120px + 30px gap)
    const scrollAmount = itemWidth * 2; // Scroll de 2 itens por vez

    const newScroll = carouselScroll + (direction * scrollAmount);
    const maxScroll = carousel.children.length * itemWidth - carousel.clientWidth;

    const clampedScroll = Math.max(0, Math.min(maxScroll, newScroll));
    setCarouselScroll(clampedScroll);

    carousel.scrollTo({
      left: clampedScroll,
      behavior: 'smooth'
    });
  };

  const handleCarouselItemClick = (circle) => {
    handleIconClick(circle);
  };

  // Função para lidar com clique nos logos do carrossel (LogoLoop)
  const handleLogoClick = (circle) => {
    console.error(`Clicou no logo do carrossel: ${circle.name} - ${circle.title}`);
    console.log(`isCarouselMode: ${isCarouselMode}`);
    
    // No modo carrossel, sempre usar CustomAlert
    if (isCarouselMode) {
      console.log(`Mostrando alerta para: ${circle.title}`);
      setAlertInfo({
        show: true,
        title: circle.title,
        content: (
          <div style={{ textAlign: 'center' }}>
            <img 
              src={circle.icon} 
              alt={circle.title} 
              style={{ 
                width: '160px', 
                height: '160px', 
                borderRadius: '25px', 
                objectFit: 'cover',
                marginBottom: '25px',
                boxShadow: '0 12px 35px rgba(0, 0, 0, 0.4)'
              }} 
            />
            
          </div>
        ),
        url: circle.url,
      });
    } else {
      console.log('Não está no modo carrossel, usando popup tradicional');
    }
  };


  const handleMouseMove = (e, circle) => {
    // Armazenar referência do elemento antes do requestAnimationFrame
    const element = e.currentTarget;
    if (!element) return;
    
    // Usar requestAnimationFrame para suavizar as atualizações
    requestAnimationFrame(() => {
      // Verificar se o elemento ainda existe e está no DOM
      if (!element || !document.contains(element)) return;
      
      try {
        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        const mouseX = e.clientX - centerX;
        const mouseY = e.clientY - centerY;
        
        // Limitar os valores para evitar rotações extremas
        const rotateX = Math.max(-30, Math.min(30, (mouseY / rect.height) * 30));
        const rotateY = Math.max(-30, Math.min(30, (mouseX / rect.width) * 30));

        element.style.setProperty('--rotate-x', `${rotateX}deg`);
        element.style.setProperty('--rotate-y', `${rotateY}deg`);
      } catch (error) {
        console.warn('Erro no handleMouseMove:', error);
      }
    });
  };

  // Adicionar throttling para melhor performance
  const throttledMouseMove = (e, circle) => {
    if (!e.currentTarget.mouseMoveTimeout) {
      e.currentTarget.mouseMoveTimeout = setTimeout(() => {
        handleMouseMove(e, circle);
        e.currentTarget.mouseMoveTimeout = null;
      }, 16); // ~60fps
    }
  };


  const handleMouseEnter = (circle) => {
    setHoveredCircle(circle.id);
    
    
    const circleElement = circleRefs.current[circle.id];
    if (circleElement) {
      circleElement.style.transform = `scale(${circle.position.hoverScale})`;
    }
  };

  const handleMouseLeave = (e) => {
    const element = e.currentTarget;
    if (!element) return;
    
    // Limpar timeout se existir
    if (element.mouseMoveTimeout) {
      clearTimeout(element.mouseMoveTimeout);
      element.mouseMoveTimeout = null;
    }
    
    // Resetar rotações para 0
    element.style.setProperty('--rotate-x', '0deg');
    element.style.setProperty('--rotate-y', '0deg');
    
    // Restaurar scale original
    const circleId = parseInt(element.getAttribute('data-circle-id'));
    const circle = circles.find(c => c.id === circleId);
    if (circle) {
      element.style.transform = `scale(${circle.position.scale})`;
    }
    
    // Resetar estado de hover
    setHoveredCircle(null);
  };

  // Ícones padrão do Apple Watch com URLs de imagens
  const defaultIcons = [
    { name: 'Phone', icon: 'https://cdn-icons-png.flaticon.com/512/724/724664.png', color: '#34C759', title: 'Telefone', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Mail', icon: 'https://cdn-icons-png.flaticon.com/512/732/732200.png', color: '#007AFF', title: 'E-mail', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Messages', icon: 'https://cdn-icons-png.flaticon.com/512/134/134914.png', color: '#34C759', title: 'Mensagens', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Breathe', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#AF52DE', title: 'Respiração', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Activity', icon: 'https://cdn-icons-png.flaticon.com/512/2092/2092063.png', color: '#FF3B30', title: 'Atividade', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Workout', icon: 'https://cdn-icons-png.flaticon.com/512/2092/2092063.png', color: '#34C759', title: 'Treino', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Settings', icon: 'https://cdn-icons-png.flaticon.com/512/126/126472.png', color: '#8E8E93', title: 'Configurações', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Timer', icon: 'https://cdn-icons-png.flaticon.com/512/2088/2088617.png', color: '#FF9500', title: 'Temporizador', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Stopwatch', icon: 'https://cdn-icons-png.flaticon.com/512/2088/2088617.png', color: '#FF9500', title: 'Cronômetro', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'World Clock', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#FF9500', title: 'Relógio Mundial', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Maps', icon: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', color: '#007AFF', title: 'Mapas', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Weather', icon: 'https://cdn-icons-png.flaticon.com/512/1163/1163665.png', color: '#007AFF', title: 'Tempo', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Alarms', icon: 'https://cdn-icons-png.flaticon.com/512/2088/2088617.png', color: '#FF9500', title: 'Alarmes', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Water Lock', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#007AFF', title: 'Bloqueio de Água', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Compass', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#FF3B30', title: 'Bússola', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Reminders', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#8E8E93', title: 'Lembretes', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Photos', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#FF3B30', title: 'Fotos', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Heart Rate', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#FF3B30', title: 'Frequência Cardíaca', iconAlreadyBackground: false, url: 'https://example.com' },
    { name: 'Music', icon: 'https://cdn-icons-png.flaticon.com/512/1828/1828843.png', color: '#FF3B30', title: 'Música', iconAlreadyBackground: false, url: 'https://example.com' },
  ];

  useEffect(() => {
    const generateCircles = () => {
      const iconsToUse = icons.length > 0 ? icons : defaultIcons;
      const sizes = getResponsiveSizes();
      
      // Layout centralizado nas laterais - esquerda e direita
      const centerY = 60; // Centro vertical da tela
      const leftX = 17; // Posição da esquerda
      const rightX = 77; // Posição da direita
      const horizontalSpacing = 20; // Espaçamento horizontal
      const verticalSpacing = 20; // Espaçamento vertical menor para centralizar melhor
      
      // Posições para a projeção específica da imagem (3 por lado com curva para dentro)
      const hexPositions = [
        // Lado esquerdo - curva para dentro
        { x: leftX - horizontalSpacing * 0.1, y: centerY - verticalSpacing * 1.5, row: 0, col: 0, side: 'left' }, // Top-Left (mais afastado)
        { x: leftX - horizontalSpacing * 0.3, y: centerY - verticalSpacing * 0.5, row: 1, col: 0, side: 'left' }, // Middle-Left (mais próximo do centro)
        { x: leftX - horizontalSpacing * 0.1, y: centerY + verticalSpacing * 0.5, row: 2, col: 0, side: 'left' }, // Bottom-Left (mais afastado)
        
        // Lado direito - curva para dentro
        { x: rightX + horizontalSpacing * 0.1, y: centerY - verticalSpacing * 1.5, row: 0, col: 1, side: 'right' }, // Top-Right (mais afastado)
        { x: rightX + horizontalSpacing * 0.3, y: centerY - verticalSpacing * 0.5, row: 1, col: 1, side: 'right' }, // Middle-Right (mais próximo do centro)
        { x: rightX + horizontalSpacing * 0.1, y: centerY + verticalSpacing * 0.5, row: 2, col: 1, side: 'right' }, // Bottom-Right (mais afastado)
      ];

      // Dividir igualmente entre esquerda e direita - otimizado para 6 ícones
      const circlesPerSide = Math.ceil(count / 2);
      const leftSideCount = Math.min(circlesPerSide, 3); // Máximo 3 por lado para a projeção específica
      const rightSideCount = Math.min(count - leftSideCount, 3); // Máximo 3 por lado para a projeção específica
      
      const circles = [];
      
      // Adicionar círculos do lado esquerdo
      for (let i = 0; i < leftSideCount; i++) {
        const iconData = iconsToUse[i % iconsToUse.length];
        const pos = hexPositions[i];
        
        circles.push({
          id: i,
          name: iconData.name,
          icon: iconData.icon,
          color: iconData.color,
          title: iconData.title,
          iconAlreadyBackground: iconData.iconAlreadyBackground || false,
          url: iconData.url,
          position: {
            top: pos.y,
            left: pos.x,
            scale: 1.0 + Math.random() * sizes.scaleRange, // Escala responsiva
            rotation: (Math.random() - 0.5) * 20, // Rotação entre -10 e 10 graus
            animationDelay: i * 0.3,
            animationDuration: 15 + Math.random() * 15, // Duração entre 15-30s
            row: pos.row,
            col: pos.col,
            side: pos.side,
            floatX: (Math.random() - 0.5) * 40, // Movimento horizontal
            floatY: (Math.random() - 0.5) * 30, // Movimento vertical
            floatSpeed: 0.5 + Math.random() * 1.5, // Velocidade de flutuação
            // Tamanhos responsivos
            baseSize: sizes.baseSize,
            iconSize: sizes.iconSize,
            hoverScale: sizes.hoverScale
          }
        });
      }
      
      // Adicionar círculos do lado direito
      for (let i = 0; i < rightSideCount; i++) {
        const iconData = iconsToUse[(leftSideCount + i) % iconsToUse.length];
        const pos = hexPositions[3 + i]; // Posições do lado direito começam no índice 3
        
        circles.push({
          id: leftSideCount + i,
          name: iconData.name,
          icon: iconData.icon,
          color: iconData.color,
          title: iconData.title,
          iconAlreadyBackground: iconData.iconAlreadyBackground || false,
          url: iconData.url,
          position: {
            top: pos.y,
            left: pos.x,
            scale: 1.0 + Math.random() * sizes.scaleRange, // Escala responsiva
            rotation: (Math.random() - 0.5) * 20, // Rotação entre -10 e 10 graus
            animationDelay: (leftSideCount + i) * 0.3,
            animationDuration: 15 + Math.random() * 15, // Duração entre 15-30s
            row: pos.row,
            col: pos.col,
            side: pos.side,
            // Parâmetros para animação de flutuação
            floatX: (Math.random() - 0.5) * 40, // Movimento horizontal
            floatY: (Math.random() - 0.5) * 30, // Movimento vertical
            floatSpeed: 0.5 + Math.random() * 1.5, // Velocidade de flutuação
            // Tamanhos responsivos
            baseSize: sizes.baseSize,
            iconSize: sizes.iconSize,
            hoverScale: sizes.hoverScale
          }
        });
      }
      
      return circles;
    };

    setCircles(generateCircles());
  }, [count, icons, screenSize]);

  // Fechar popup ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupCircle !== null) {
        // Verificar se o clique foi fora de qualquer floating circle e popup
        const isClickOnCircle = event.target.closest('.floating-circle');
        const isClickOnPopup = event.target.closest('.popup-window');
        if (!isClickOnCircle && !isClickOnPopup) {
          setPopupCircle(null);
        }
      }
    };

    if (popupCircle !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupCircle]);

  // Recalcular posição do popup quando a janela for redimensionada
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      setScreenSize({ width: newWidth, height: newHeight });
      const newCarouselMode = newWidth < 1200;
      console.log(`Redimensionamento: largura=${newWidth}, isCarouselMode=${newCarouselMode}`);
      setIsCarouselMode(newCarouselMode);

      if (popupCircle !== null) {
        calculatePopupPosition(popupCircle);
      }
    };

    // Executar uma vez no mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [popupCircle]);

  return (
    <>
      {!isCarouselMode ? (
        // Modo desktop: círculos flutuantes nas laterais
        <div ref={containerRef} className="apple-watch-floating-container">
          {circles.map((circle) => (
            <div
              key={circle.id}
              ref={(el) => { circleRefs.current[circle.id] = el; }}
              data-circle-id={circle.id}
              data-has-background={circle.iconAlreadyBackground}
              className={`floating-circle circle-row-${circle.position.row} circle-col-${circle.position.col} three-3d-card`}
              style={{
                position: 'absolute',
                top: `${circle.position.top}%`,
                left: `${circle.position.left}%`,
                transform: `scale(${circle.position.scale}) rotate(${circle.position.rotation}deg)`,
                animationDelay: `${circle.position.animationDelay}s`,
                animationDuration: `${circle.position.animationDuration}s`,
                zIndex: 5,
                '--float-x': `${circle.position.floatX}px`,
                '--float-y': `${circle.position.floatY}px`,
                '--float-speed': `${circle.position.floatSpeed}s`,
                '--glow-color': circle.color,
                '--rotate-x': '0deg',
                '--rotate-y': '0deg'
              }}
              onClick={() => handleIconClick(circle)}
              onMouseMove={(e) => handleMouseMove(e, circle)}
              onMouseLeave={handleMouseLeave}
              onMouseEnter={() => handleMouseEnter(circle)}
              title={circle.title}
            >
              <div
                className="circle-inner"
                style={{
                  backgroundColor: circle.iconAlreadyBackground ? 'transparent' : circle.color,
                  boxShadow: circle.iconAlreadyBackground ? 'none' : `0 0 20px ${circle.color}40, inset 0 1px 0 rgba(255,255,255,0.3)`,
                  cursor: 'pointer',
                  width: `${circle.position.baseSize}px`,
                  height: `${circle.position.baseSize}px`
                }}
              >
                <img
                  className="circle-icon"
                  src={circle.icon}
                  alt={circle.title}
                />
                <div className="circle-glow"></div>
              </div>
            </div>
          ))}
        </div>
       ) : (
         // Modo mobile/tablet: carrossel usando LogoLoop
         <div className={`apple-watch-carousel-container apple-watch-carousel-${position}`}>
           {(() => {
             const carouselProps = getCarouselResponsiveProps();
             return (
               <LogoLoop
                 logos={circles.map(circle => {
                   console.log(`Mapeando círculo para LogoLoop: ${circle.title}`);
                   return {
                     src: circle.icon,
                     alt: circle.title,
                     title: circle.title,
                     onClick: () => {
                       console.log(`LogoLoop onClick chamado para: ${circle.title}`);
                       handleLogoClick(circle);
                     },
                   };
                 })}
                 speed={carouselProps.speed}
                 direction="right"
                 width="100%"
                 logoHeight={carouselProps.logoHeight}
                 gap={carouselProps.gap}
                 pauseOnHover={true}
                 fadeOut={false}
                 scaleOnHover={true}
                 className="apple-watch-logoloop"
               />
             );
           })()}
         </div>
       )}

      {/* Janela pop-up - renderizada fora do container para ter z-index independente */}
      {popupCircle !== null && (() => {
        const selectedCircle = circles.find(c => c.id === popupCircle);

        return (
          <div
            className={`popup-window popup-${popupPosition.side}`}
            style={{
              backgroundColor: selectedCircle?.color,
              '--popup-color': selectedCircle?.color,
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
              position: 'fixed',
              zIndex: 999999
            }}
          >
            <div className="popup-content">
              <div className="popup-title">
                {selectedCircle?.title}
              </div>
              <button
                className="popup-arrow-button"
                onClick={handleClosePopup}
              >
                <span className="arrow-icon">→</span>
              </button>
            </div>
          </div>
        );
      })()}

      <CustomAlert
        show={alertInfo.show}
        title={alertInfo.title}
        onClose={() => setAlertInfo({ show: false, title: '', content: null, url: '' })}
        onConfirm={() => {
          if (alertInfo.url) {
            window.open(alertInfo.url, '_blank');
          }
          setAlertInfo({ show: false, title: '', content: null, url: '' });
        }}
        confirmButtonText="Toque Aqui"
        cancelButtonText="Fechar"
        showCancelButton={true}
      >
        {alertInfo.content}
      </CustomAlert>
    </>
  );
};

export default AppleWatchFloatingCircle;
